
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import External from 'plaid-threads/Icons/External';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Retrieve real-time balance information'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Balance'}>
  {metadata.description}
    </Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Balance'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/signal/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='/docs/quickstart' />
  <Actions.SmallCard icon={<External mdxType="External" />} title='Demo' link='https://plaid.coastdemo.com/share/6786ccc5a048a5f1cf748cb5?zoom=100' /> 
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Balance is Plaid's product for receiving real-time balance information. This data is commonly used to tell if an account has sufficient funds before using it as a funding source for a payment transaction, helping you to avoid ACH returns. Balance is available for use exclusively in combination with other Plaid products, such as `}<a parentName="p" {...{
        "href": "/docs/auth"
      }}>{`Auth`}</a>{` for money movement or account funding use cases or `}<a parentName="p" {...{
        "href": "/docs/transactions"
      }}>{`Transactions`}</a>{` for personal finance use cases.`}</p>
    <p>{`There are two ways to retrieve real-time balance, depending on your use case. To check balance for the risk of a proposed ACH payment, use `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, which evaluates the balance against your `}<a parentName="p" {...{
        "href": "/docs/signal/signal-rules/"
      }}>{`Signal Rules`}</a>{`; this is also the path to later add `}<a parentName="p" {...{
        "href": "/docs/signal"
      }}>{`Signal Transaction Scores`}</a>{` with no re-integration. For all other use cases, such as personal finance management, treasury, or simply displaying a balance, use `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{`. For more details, see `}<a parentName="p" {...{
        "href": "#balance-integration-options"
      }}>{`Balance integration options`}</a>{`.`}</p>
    <YouTubeEmbed videoId='VfXwIkNsqO8' alignment='full' title='Plaid Balance in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Balance works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <h4 {...{
      "id": "cached-and-real-time-balance"
    }}>{`Cached and real-time balance`}</h4>
    <p>{`While you can retrieve balance data via many endpoints, including by calling the free-to-use `}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{` endpoint, this data is cached, making it unsuitable for situations where you need real-time balance information, except immediately after the Item has been added.`}</p>
    <p>{`An Item with Transactions may update cached balances once a day or more, while an Item with only Auth may update cached balances every 30 days or even less frequently. `}</p>
    <p>{`For retrieving real-time balance for payments use cases, only Balance should be used. `}</p>
    <p>{`Because Balance always performs a real-time data extraction, latency is higher than other Plaid endpoints (p50 ~3s, p95 ~11s). To check for ACH return risk in critical user-present flows that require low latency, consider `}<a parentName="p" {...{
        "href": "/docs/signal"
      }}>{`Signal Transaction Scores`}</a>{`. For more details, see `}<a parentName="p" {...{
        "href": "#reducing-latency-and-getting-better-insights-with-signal-transaction-scores"
      }}>{`Reducing latency and getting better insights with Signal Transaction Scores`}</a>{`. `}</p>
    <h4 {...{
      "id": "balance-integration-options"
    }}>{`Balance integration options`}</h4>
    <p>{`There are two options for retrieving balance data, each designed for a different use case: `}</p>
    <ul>
      <li parentName="ul">{`If you are checking balance to avoid ACH returns on a transaction, use `}<inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{`. At no additional cost, this endpoint gives you access to a suite of powerful tools designed for ACH risk management, including Dashboard-configurable rules to automatically accept or reject proposed transactions and interactive models to help you determine the best thresholds for your business. It also future-proofs your integration by being fully compatible (no code changes required) with the more sophisticated, lower-latency models used by `}<a parentName="li" {...{
          "href": "/docs/signal"
        }}>{`Signal Transaction Scores`}</a>{`.`}</li>
      <li parentName="ul">{`For personal finance management or treasury management use cases, use `}<inlineCode parentName="li">{`/accounts/balance/get`}</inlineCode>{`. This endpoint just returns real-time balance without providing any additional functionality.  `}</li>
    </ul>
    <p>{`Both return the same account data at the same price and latency, so your choice should be driven by use case rather than cost or performance.`}</p>
    <h5 {...{
      "id": "balance-using-signalevaluate"
    }}>{`Balance using /signal/evaluate`}</h5>
    <p>{`The `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` endpoint is the recommended way to retrieve balance data whenever you are checking balance to evaluate the insufficient funds risk of a proposed ACH transaction. `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` checks balance and returns current and available balance, as well as a recommended action to take (i.e., accept the transaction, review it further, or request a different payment method) based on a ruleset evaluation using `}<a parentName="p" {...{
        "href": "/docs/signal/signal-rules/"
      }}>{`Signal Rules`}</a>{`. With Signal Rules, you can easily customize your business logic around these recommended actions in a Dashboard-based UI, without making any code changes. `}</p>
    <Image src='/assets/img/docs/balance-rule-editor.png' caption='Using Balance with Signal Rules in the Dashboard' expandable={true} mdxType="Image" />
    <Callout mdxType="Callout">
      <p><inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` is available for all new Balance customers who received Production access after October 15, 2025. If you are an existing Balance customer and would like access to `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, contact your account manager; for more details, see the `}<a parentName="p" {...{
          "href": "/docs/balance/migration-guide"
        }}>{`migration guide`}</a>{`.`}</p>
    </Callout>
    <h5 {...{
      "id": "balance-using-accountsbalanceget"
    }}>{`Balance using /accounts/balance/get`}</h5>
    <p><inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` retrieves balance data, including current and available balance, but does not run Signal Rules. This endpoint is recommended for all use cases where you are `}<em parentName="p">{`not`}</em>{` checking balance to evaluate a proposed ACH transaction: for example, if you need real-time balance data for a Personal Financial Management (PFM) use case, or for treasury management, or for any non-US bank account. `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` is also the legacy endpoint for checking balance that many customers use in existing integrations.`}</p>
    <h4 {...{
      "id": "integration-overview-using-signalevaluate"
    }}>{`Integration overview using /signal/evaluate`}</h4>
    <ol>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "/docs/signal/signal-rules/"
          }}>{`Create a Balance-only Rule`}</a>{` in the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/signal/risk-profiles"
          }}>{`Signal Rules Dashboard`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. Along with any other parameters you specify, make sure to include the following:`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Include `}<inlineCode parentName="li">{`signal`}</inlineCode>{` and `}<inlineCode parentName="li">{`auth`}</inlineCode>{` in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array, along with any other Plaid product(s) you plan to use. Do `}<em parentName="li">{`not`}</em>{` include `}<inlineCode parentName="li">{`balance`}</inlineCode>{` in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array.  `}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the client side, create an instance of Link using the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` returned by `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`; for more details, see the `}<a parentName="p" {...{
            "href": "/docs/link"
          }}>{`Link documentation`}</a>{`. `}</p>
        <ul parentName="li">
          <li parentName="ul">{`If you will be using the linked Item as a payment source or destination, it is recommended to `}<a parentName="li" {...{
              "href": "/docs/link/customization/"
            }}>{`configure your Link customization`}</a>{` to use the setting `}<a parentName="li" {...{
              "href": "/docs/link/customization/#account-select"
            }}>{`Account Select: Single select`}</a>{`. Otherwise, you will need to build a UI within your app for the user to indicate which account they want to use for the payment, if their bank login is associated with more than one bank account. `}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Once the end user has completed the Link flow, exchange the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` by calling `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` and `}<a parentName="p" {...{
            "href": "/docs/signal/signal-rules/#using-signal-ruleset-results"
          }}>{`determine the next steps based on results`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "/docs/signal/reporting-returns"
          }}>{`Report ACH returns and decisions`}</a>{` to Plaid.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional) After launch, periodically `}<a parentName="p" {...{
            "href": "/docs/signal/tuning-rules"
          }}>{`review and tune your Signal Rules`}</a>{` using the Dashboard. `}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "integration-overview-using-accountsbalanceget"
    }}>{`Integration overview using /accounts/balance/get`}</h4>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` with the product(s) you plan to use. Do `}<em parentName="p">{`not`}</em>{` include `}<inlineCode parentName="p">{`balance`}</inlineCode>{` in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`On the client side, create an instance of Link using the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` returned by `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`; for more details, see the `}<a parentName="p" {...{
            "href": "/docs/link"
          }}>{`Link documentation`}</a>{`. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Once the end user has completed the Link flow, exchange the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` by calling `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{`, passing in the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` returned in step 3.`}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "reducing-latency-and-getting-better-insights-with-signal-transaction-scores"
    }}>{`Reducing latency and getting better insights with Signal Transaction Scores`}</h4>
    <p>{`For use cases that involve evaluating a proposed ACH transaction to reduce return risk, Balance can be used alongside `}<a parentName="p" {...{
        "href": "/docs/signal"
      }}>{`Signal Transaction Scores`}</a>{`. Signal Transaction Scores has lower latency (p95 <2s versus Balance's 11s), making it ideal for user-present transactions in critical flows such as onboarding or purchasing. With Signal Transaction Scores, you can access 80+ criteria for rule generation and ML-powered recommendations and insights to help you optimize and fine-tune your transaction approval logic. `}</p>
    <p>{`Signal Transaction Scores and Balance are both part of Plaid Signal, Plaid's solution for ACH risk management. You can purchase and use either Balance or Signal Transaction Scores by itself, or combine them for a more comprehensive ACH risk management approach. `}</p>
    <p>{`Once you have integrated Balance using `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, Signal Transaction Scores requires no additional engineering work to integrate. Just use the Signal Rule editor in the Dashboard to upgrade your existing Balance-only rule into a Signal Transaction Score-powered rule. `}</p>
    <p>{`To learn more, or to request access to Signal Transaction Scores, contact `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`sales`}</a>{` or your account manager.`}</p>
    <h4 {...{
      "id": "adding-balance-to-existing-items"
    }}>{`Adding Balance to existing Items`}</h4>
    <p>{`If you are an existing Plaid customer and would like to get real-time balance data for an existing Item, simply call `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` on the Item. `}</p>
    <p>{`If you would like to use `}<a parentName="p" {...{
        "href": "#balance-using-signalevaluate"
      }}>{`Signal Rules`}</a>{` with a Balance-only ruleset to evaluate a balance on an existing Item that was not initialized with `}<inlineCode parentName="p">{`signal`}</inlineCode>{`, you can call `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` on the Item directly; evaluating a balance does not require the risk product disclosure. If you upgrade the ruleset to use `}<a parentName="p" {...{
        "href": "/docs/signal"
      }}>{`Signal Transaction Scores`}</a>{`, the user must first agree to the risk product disclosure; for more details, see `}<a parentName="p" {...{
        "href": "/docs/signal/creating-signal-items/#adding-signal-to-existing-items"
      }}>{`Adding Signal to existing Items`}</a>{`. `}</p>
    <h4 {...{
      "id": "migration-guide"
    }}>{`Migration guide`}</h4>
    <p>{`If you are an existing customer and would like to migrate from `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` to `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` to take advantage of Signal Rules, see the `}<a parentName="p" {...{
        "href": "/docs/balance/migration-guide"
      }}>{`Balance migration guide`}</a>{`.`}</p>
    <h4 {...{
      "id": "current-and-available-balance"
    }}>{`Current and available balance`}</h4>
    <p>{`Banks represent balance with two separate values, current balance and available balance. For assessing ACH risk, available balance is often more important than current balance, as it represents predicted balance net of any pending transactions, while current balance does not take pending transactions into account.`}</p>
    <p>{`For credit accounts, available balance indicates the amount that can be spent without hitting the credit limit (net of any pending transactions), while in investment accounts, it indicates the total available cash.`}</p>
    <p>{`In some cases, a financial institution may not provide available balance information. If necessary, you can often calculate available balance by starting with the current balance, then using the `}<a parentName="p" {...{
        "href": "/docs/transactions"
      }}>{`Transactions`}</a>{` product to detect any pending transactions and adjusting the balance accordingly.`}</p>
    <h5 {...{
      "id": "typical-balance-fill-rates-by-field"
    }}>{`Typical balance fill rates by field`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Typical fill rate`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Current balance`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`99%`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Available balance`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`91%`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "balance-pricing"
    }}>{`Balance pricing`}</h4>
    <p>{`Balance is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#per-request-flat-fee"
      }}>{`flat fee model`}</a>{`, meaning you will be billed once for each successful call to `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` or `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{`. `}</p>
    <p><inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` (when used with a Balance-only ruleset) and `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` are billed at the same rate.`}</p>
    <p>{`When using `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, you will be billed if the API call is successful, even if balance extraction fails.`}</p>
    <p>{`To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`To get started building with Balance, see `}<a parentName="p" {...{
        "href": "/docs/balance/add-to-app"
      }}>{`Add Balance to your App`}</a>{`.`}</p>
    <p>{`If you're ready to launch to Production, see the Launch Center.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle="See next steps to launch in Production" link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "cached-and-real-time-balance",
        level: 4,
        title: "Cached and real-time balance",
      },
{
        id: "balance-integration-options",
        level: 4,
        title: "Balance integration options",
      },
{
        id: "integration-overview-using-signalevaluate",
        level: 4,
        title: "Integration overview using /signal/evaluate",
      },
{
        id: "integration-overview-using-accountsbalanceget",
        level: 4,
        title: "Integration overview using /accounts/balance/get",
      },
{
        id: "reducing-latency-and-getting-better-insights-with-signal-transaction-scores",
        level: 4,
        title: "Reducing latency and getting better insights with Signal Transaction Scores",
      },
{
        id: "adding-balance-to-existing-items",
        level: 4,
        title: "Adding Balance to existing Items",
      },
{
        id: "migration-guide",
        level: 4,
        title: "Migration guide",
      },
{
        id: "current-and-available-balance",
        level: 4,
        title: "Current and available balance",
      },
{
        id: "balance-pricing",
        level: 4,
        title: "Balance pricing",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
